import { NextSeo } from "next-seo";
import { addCacheTag } from '@vercel/functions';
import FeaturedBlogCard from '@c/cards/FeaturedBlogCard';
import { DefaultLayout } from '@c/layouts';
import dynamic from 'next/dynamic';
import Select from '@ui/Select';

const BlogLink = dynamic(() => import('@ui/Link/BlogLink'), {
  ssr: false,
});
import { BlogDocument } from '@util/types/firestore/blog';
import { GetServerSideProps } from 'next';
import { useEffect, useState } from 'react';
import { CursorPagination, getAllCategories, getBlogs } from '@util/blog';
import Button from '@ui/Button';
import { useRouter } from 'next/router';
import { encodeQueryData } from '@util/urlHelpers';
import BlogNativeAd from '@c/advertisements/BlogNativeAd';
import { AdType, getNAds } from '@util/firestore/ads';
import { cleanHtmlString } from '@util/htmlHelpers';

interface BlogPageProps {
  posts: Partial<BlogDocument>[];
  pageInfo: CursorPagination<string>;
  category: string | null;
  categories: { name: string; slug: string }[]
}

const BlogPage = ({ posts, pageInfo, category: initialCategory, categories = [] }: BlogPageProps) => {
  const router = useRouter();
  const featuredIndex = Math.max(posts.findIndex((p) => p.is_featured), 0);
  const emptyPosts = posts.length === 0;
  const showPaginator = pageInfo.totalResults > 7;
  const featuredPost = posts[featuredIndex];
  const otherPosts = posts.filter((p, i) => i !== featuredIndex);
  const [category, setCategory] = useState(initialCategory ?? 'all');
  const [ads, setAds] = useState<AdType[] | null>(null);

  useEffect(() => {
    const fetchAds = async () => {
      const adResults = await getNAds(3, 'block');
      setAds(adResults);
    };

    fetchAds();
  }, []);

  function handleCategoryChange(slug: string) {
    const query: Record<string, string> = {};

    if (slug !== 'all') query.category = slug;

    router.push({
      pathname: '/blog',
      query,
    });
  }

  return (
    <>
      <NextSeo
        title="GearFocus Blog - Camera Gear Tips, Reviews & Seller Advice"
        description="Stay updated with the latest in photography and videography. Explore expert gear reviews, how-to guides, seller tips, and creative inspiration from the GearFocus community."
        canonical="https://gearfocus.com/blog"
        openGraph={{
          images: [
            {
              url: "https://gearfocus.com/gear_pile.png",
              width: 800,
              height: 600,
              alt: "Camera Gear Tips, Reviews & Seller Advice",
              type: "image/jpeg",
            },
          ],
          siteName: "GearFocus Marketplace",
          type: 'website',
          url: "https://gearfocus.com/blog",
        }}
      />

      <div className="
        mb-[2rem] mt-[6rem] 
        flex-col sm:flex
        gap-[1.2rem]
        w-full max-w-default
        px-[2rem] md:px-[4rem] xl:px-default-padding-x
      ">
        {/* top bar */}
        <div className="mx-4 flex flex-col justify-between gap-[1.6rem] sm:mx-0 sm:flex-row sm:items-center sm:gap-0" itemScope itemType="http://schema.org/Blog">
          <h1 className="text-[3.2rem] font-semibold sm:text-[4.4rem]">
            GearFocus Blog
          </h1>
          {/* controls */}
          <div className="flex h-fit w-full max-w-[67.4rem] flex-col gap-[1.4rem] sm:flex-row sm:justify-end">
            <div>
              <Select
                value={category}
                isLoading={categories.length < 1}
                onChange={(cat) => handleCategoryChange(cat.value)}
                options={[{ name: 'All Categories', slug: 'all' }, ...categories].map((c) => ({
                  label: cleanHtmlString(c.name),
                  value: c.slug,
                  id: c.slug,
                }))}
                allowEmpty={false}
              />
            </div>
          </div>
        </div>
        {/* Empty */}
        {emptyPosts && (
          <div className="mt-[4rem] flex flex-col items-center justify-center sm:mt-[6rem]">
            <h1 className="text-[2.4rem] font-semibold sm:text-[3.2rem]">
              No results found
            </h1>
            <p className="mt-[1.6rem] text-[1.6rem] text-brand-gray sm:text-[2rem]">
              Try searching for something else
            </p>
          </div>
        )}
        {/* results */}
        {!emptyPosts && (
          <div className="mt-[4rem] sm:mt-[6rem]" itemScope itemType="http://schema.org/BlogPosting">
            {/* featured post */}
            <div key={featuredPost.id} className="mb-[4rem] lg:mb-[6rem] w-full grow ">
              <FeaturedBlogCard blog={featuredPost} />
            </div>

            <div className="sm:hidden h-[0.5rem] w-full bg-brand-lightest-gray mb-[4rem] lg:mb-[6rem]"></div>

            {/* other posts */}
            <div className="flex flex-col flex-wrap gap-[6rem] sm:flex-row sm:justify-space-between">
              <div className="
              gap-[2rem]
              grid grid-cols-2
              sm:grid-cols-2
              md:grid-cols-3 
            ">
                {otherPosts.map((post) => {
                  return <BlogLink key={post.id} blog={post} />;
                })}
              </div>
            </div>
          </div>
        )}

        {/* pagination */}
        {showPaginator && (
          <div className="my-[4rem] flex w-full">
            {pageInfo.prev && (
              <Button
                text="Previous"
                type="secondary"
                disabled={!pageInfo.prev}
                href={`/blog?direction=prev&cursor=${pageInfo.prev}${category && category !== "all" ? `&category=${category}` : ''}`}
              />
            )}
            {pageInfo.next && (
              <div className="ml-auto">
                <Button
                  text="Next"
                  type="secondary"
                  disabled={!pageInfo.next}
                  href={`/blog?direction=next&cursor=${pageInfo.next}${category && category !== "all" ? `&category=${category}` : ''}`}
                />
              </div>
            )}
          </div>
        )}

        <div className="w-full">
          <div className="h-[0.5rem] w-full bg-brand-lightest-gray"></div>
          <div className="mt-[4rem] flex gap-[2rem] w-full flex-col items-center lg:flex-row">
            {ads ? (
              ads.map((ad, index) => (
                <div key={`ad-${index}`}>
                  <BlogNativeAd
                    id={ad.id || 'unknown'}
                    title={ad.title}
                    src={ad.src}
                    href={ad.url}
                    vendor={ad.vendor || 'unknown'}
                  />
                </div>
              ))
            ) : (
              Array(3)
                .fill(0)
                .map((_, i) => (
                  <div key={i} className="h-[24rem] w-[32rem] animate-pulse rounded-[1rem] bg-brand-lightest-gray" />
                ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

BlogPage.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export const getServerSideProps: GetServerSideProps<BlogPageProps> = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=300, stale-while-revalidate=900'
  );

  const { category: qCategory, direction, cursor } = context.query;

  let pageInfoArgs;
  if (direction && cursor) {
    pageInfoArgs = {
      cursor: cursor as string,
      direction: direction as 'next' | 'prev',
    };
  }

  const { results, pageInfo } = await getBlogs(typeof qCategory === 'string' ? qCategory : null, pageInfoArgs?.cursor, pageInfoArgs?.direction);
  const categories = await getAllCategories()

  // Add cache tags for on-demand invalidation
  addCacheTag("blog");

  return {
    props: {
      posts: results,
      pageInfo,
      category: typeof qCategory === 'string' ? qCategory : null,
      categories
    },
    //revalidate: 86400
  };
};

export default BlogPage;